// src/contexts/NotificationContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import api from '../services/api';
import { getRoleLabel } from '../utils/roleLabels';

const NotificationContext = createContext();

export const useNotificationContext = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotificationContext must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { user } = useAuth();
    const { showInfo, showSuccess, showWarning, showError } = useToast();

    // ===== FUNCIONES PARA NOTIFICACIONES (Persistentes en BD) =====
    // Estas se almacenan en la base de datos y aparecen en la campanita

    const createNotification = useCallback(async (notificationData) => {
        try {
            await api.post('/notifications', notificationData);
        } catch (error) {
            console.error('Error creando notificación:', error);
        }
    }, []);

    const createNotificationForRole = useCallback(async (role, notificationData) => {
        try {
            await api.post(`/notifications/role/${role}`, notificationData);
        } catch (error) {
            console.error(`Error creando notificación para rol ${role}:`, error);
        }
    }, []);

    const createNotificationForAdmins = useCallback(async (notificationData) => {
        try {
            await api.post('/notifications/admins', notificationData);
        } catch (error) {
            console.error('Error creando notificación para admins:', error);
        }
    }, []);

    // ===== FUNCIONES PARA NOTIFICACIONES (Persistentes en BD) =====
    // Estas se almacenan en la base de datos y aparecen en la campanita

    const notifyActivoCreated = useCallback((activo) => {
        createNotification({
            userId: user?.id,
            tipo: 'asset',
            titulo: 'Nuevo activo registrado',
            mensaje: `Se ha registrado un nuevo activo: ${activo.nombre || activo.codigo}`,
            refTipo: 'Activo',
            refId: activo.id,
            ruta: `/activos/${activo.id}`
        });
    }, [createNotification, user?.id]);

    const notifyActivoAssigned = useCallback((activo, usuario) => {
        if (!activo || !usuario) {
            console.warn('notifyActivoAssigned: activo o usuario es undefined');
            return;
        }

        createNotification({
            userId: usuario.id,
            tipo: 'assignment',
            titulo: 'Activo asignado',
            mensaje: `Se te ha asignado el activo: ${activo.nombre || activo.codigo}`,
            refTipo: 'Activo',
            refId: activo.id,
            ruta: `/activos/${activo.id}`
        });
    }, [createNotification]);

    const notifyTicketCreated = useCallback((ticket) => {
        createNotification({
            userId: user?.id,
            tipo: 'ticket',
            titulo: 'Nuevo ticket creado',
            mensaje: `Se ha creado un nuevo ticket: ${ticket.descripcion || `#${ticket.id}`}`,
            refTipo: 'Ticket',
            refId: ticket.id,
            ruta: `/tickets/${ticket.id}`
        });
    }, [createNotification, user?.id]);

    const notifyTicketAssigned = useCallback((ticket, usuario) => {
        createNotification({
            userId: usuario.id,
            tipo: 'ticket',
            titulo: 'Ticket asignado',
            mensaje: `Se te ha asignado el ticket: ${ticket.descripcion || `#${ticket.id}`}`,
            refTipo: 'Ticket',
            refId: ticket.id,
            ruta: `/tickets/${ticket.id}`
        });
    }, [createNotification]);

    const notifyTicketUpdated = useCallback((ticket) => {
        createNotification({
            userId: user?.id,
            tipo: 'ticket',
            titulo: 'Ticket actualizado',
            mensaje: `El ticket ${ticket.descripcion || `#${ticket.id}`} ha sido actualizado`,
            refTipo: 'Ticket',
            refId: ticket.id,
            ruta: `/tickets/${ticket.id}`
        });
    }, [createNotification, user?.id]);

    const notifyUserCreated = useCallback((usuario) => {
        createNotification({
            userId: user?.id,
            tipo: 'user',
            titulo: 'Nuevo usuario registrado',
            mensaje: `Se ha registrado un nuevo usuario: ${usuario.nombre || usuario.username}`,
            refTipo: 'Usuario',
            refId: usuario.id,
            ruta: `/usuarios/${usuario.id}`
        });
    }, [createNotification, user?.id]);

    const notifySystemUpdate = useCallback((message) => {
        createNotification({
            userId: user?.id,
            tipo: 'system',
            titulo: 'Actualización del sistema',
            mensaje: message,
            refTipo: 'Sistema',
            refId: null,
            ruta: null
        });
    }, [createNotification, user?.id]);

    // ===== FUNCIONES PARA NOTIFICACIONES DE PAZ Y SALVO =====
    const notifyPazYSalvoCreated = useCallback((pazYSalvo) => {
        createNotification({
            userId: user?.id,
            tipo: 'pazysalvo',
            titulo: 'Paz y Salvo creado',
            mensaje: `Se ha creado un nuevo Paz y Salvo para ${pazYSalvo.usuarioNombre}`,
            refTipo: 'PazYSalvo',
            refId: pazYSalvo.id,
            ruta: `/pazysalvo/${pazYSalvo.id}`
        });
    }, [createNotification, user?.id]);

    const notifyPazYSalvoEnviadoAFirma = useCallback((pazYSalvo) => {
        // Notificar a todos los firmantes
        pazYSalvo.firmas?.forEach(firma => {
            if (firma.firmanteUserId) {
                createNotification({
                    userId: firma.firmanteUserId,
                    tipo: 'pazysalvo',
                    titulo: 'Paz y Salvo enviado a firma',
                    mensaje: `Se requiere tu firma para el Paz y Salvo de ${pazYSalvo.usuarioNombre}`,
                    refTipo: 'PazYSalvo',
                    refId: pazYSalvo.id,
                    ruta: `/pazysalvo/${pazYSalvo.id}`
                });
            }
        });
    }, [createNotification]);

    const notifyPazYSalvoFirmado = useCallback((pazYSalvo, firmante) => {
        createNotification({
            userId: user?.id,
            tipo: 'pazysalvo',
            titulo: 'Paz y Salvo firmado',
            mensaje: `${firmante.nombre} ha firmado el Paz y Salvo de ${pazYSalvo.usuarioNombre}`,
            refTipo: 'PazYSalvo',
            refId: pazYSalvo.id,
            ruta: `/pazysalvo/${pazYSalvo.id}`
        });
    }, [createNotification, user?.id]);

    const notifyPazYSalvoAprobado = useCallback((pazYSalvo) => {
        createNotification({
            userId: user?.id,
            tipo: 'pazysalvo',
            titulo: 'Paz y Salvo aprobado',
            mensaje: `El Paz y Salvo de ${pazYSalvo.usuarioNombre} ha sido aprobado`,
            refTipo: 'PazYSalvo',
            refId: pazYSalvo.id,
            ruta: `/pazysalvo/${pazYSalvo.id}`
        });
    }, [createNotification, user?.id]);

    const notifyPazYSalvoRechazado = useCallback((pazYSalvo, motivo) => {
        createNotification({
            userId: user?.id,
            tipo: 'pazysalvo',
            titulo: 'Paz y Salvo rechazado',
            mensaje: `El Paz y Salvo de ${pazYSalvo.usuarioNombre} ha sido rechazado. Motivo: ${motivo}`,
            refTipo: 'PazYSalvo',
            refId: pazYSalvo.id,
            ruta: `/pazysalvo/${pazYSalvo.id}`
        });
    }, [createNotification, user?.id]);

    const notifyPazYSalvoCerrado = useCallback((pazYSalvo) => {
        createNotification({
            userId: user?.id,
            tipo: 'pazysalvo',
            titulo: 'Paz y Salvo cerrado',
            mensaje: `El Paz y Salvo de ${pazYSalvo.usuarioNombre} ha sido cerrado exitosamente`,
            refTipo: 'PazYSalvo',
            refId: pazYSalvo.id,
            ruta: `/pazysalvo/${pazYSalvo.id}`
        });
    }, [createNotification, user?.id]);

    const notifyPazYSalvoObservado = useCallback((pazYSalvo, observacion) => {
        createNotification({
            userId: user?.id,
            tipo: 'pazysalvo',
            titulo: 'Paz y Salvo observado',
            mensaje: `Se ha agregado una observación al Paz y Salvo de ${pazYSalvo.usuarioNombre}: ${observacion}`,
            refTipo: 'PazYSalvo',
            refId: pazYSalvo.id,
            ruta: `/pazysalvo/${pazYSalvo.id}`
        });
    }, [createNotification, user?.id]);

    const notifyPazYSalvoExcepcionCreada = useCallback((pazYSalvo) => {
        createNotification({
            userId: user?.id,
            tipo: 'pazysalvo',
            titulo: 'Excepción creada',
            mensaje: `Se ha creado una excepción para el Paz y Salvo de ${pazYSalvo.usuarioNombre}`,
            refTipo: 'PazYSalvo',
            refId: pazYSalvo.id,
            ruta: `/pazysalvo/${pazYSalvo.id}`
        });
    }, [createNotification, user?.id]);

    const notifyPazYSalvoUploaded = useCallback((usuario, activosPendientes = []) => {
        const message = activosPendientes.length > 0
            ? `Paz y salvo subido para ${usuario.nombre}. ${activosPendientes.length} activos pendientes de devolución.`
            : `Paz y salvo subido para ${usuario.nombre}`;
        createNotification({
            userId: user?.id,
            tipo: 'pazysalvo',
            titulo: 'Paz y Salvo subido',
            mensaje: message,
            refTipo: 'PazYSalvo',
            refId: usuario.id,
            ruta: `/pazysalvo`
        });
    }, [createNotification, user?.id]);

    const notifyActivoReturned = useCallback((activo, usuario) => {
        createNotification({
            userId: user?.id,
            tipo: 'return',
            titulo: 'Activo devuelto',
            mensaje: `El activo ${activo.codigo} ha sido devuelto por ${usuario.nombre}`,
            refTipo: 'Activo',
            refId: activo.id,
            ruta: `/activos/${activo.id}`
        });
    }, [createNotification, user?.id]);

    // ===== FUNCIONES PARA NOTIFICACIONES DE ACTAS =====
    const notifyActaRechazada = useCallback((acta, usuario) => {
        createNotification({
            userId: usuario.id,
            tipo: 'acta',
            titulo: 'Acta rechazada',
            mensaje: `Tu acta para el activo ${acta.activo?.codigo || 'N/A'} ha sido rechazada. Revisa los comentarios y vuelve a subirla.`,
            refTipo: 'Acta',
            refId: acta.id,
            ruta: `/actas/${acta.id}`
        });
    }, [createNotification]);

    const notifyActaFirmada = useCallback((acta, usuario) => {
        createNotificationForAdmins({
            userId: 0,
            tipo: 'acta',
            titulo: 'Acta firmada por usuario',
            mensaje: `${usuario.nombre} ${usuario.apellido} ha firmado un acta para el activo ${acta.activo?.codigo || 'N/A'}`,
            refTipo: 'Acta',
            refId: acta.id,
            ruta: `/actas/${acta.id}`
        });
    }, [createNotificationForAdmins]);

    // ===== FUNCIONES PARA NOTIFICAR A ADMINS =====
    const notifyAdminsTicketCreated = useCallback((ticket) => {
        createNotificationForAdmins({
            userId: 0,
            tipo: 'ticket',
            titulo: 'Nuevo ticket creado',
            mensaje: `Se ha creado un nuevo ticket: ${ticket.descripcion || `#${ticket.id}`} por ${ticket.nombreSolicitante}`,
            refTipo: 'Ticket',
            refId: ticket.id,
            ruta: `/tickets/${ticket.id}`
        });
    }, [createNotificationForAdmins]);

    const notifyAdminsActivoCreated = useCallback((activo) => {
        createNotificationForAdmins({
            userId: 0,
            tipo: 'asset',
            titulo: 'Nuevo activo registrado',
            mensaje: `Se ha registrado un nuevo activo: ${activo.nombre || activo.codigo} por ${user?.nombre || user?.username}`,
            refTipo: 'Activo',
            refId: activo.id,
            ruta: `/activos/${activo.id}`
        });
    }, [createNotificationForAdmins, user?.nombre, user?.username]);

    const notifyAdminsUserCreated = useCallback((usuario) => {
        createNotificationForAdmins({
            userId: 0,
            tipo: 'user',
            titulo: 'Nuevo usuario registrado',
            mensaje: `Se ha registrado un nuevo usuario: ${usuario.nombre} ${usuario.apellido} (${getRoleLabel(usuario.role)})`,
            refTipo: 'Usuario',
            refId: usuario.id,
            ruta: `/usuarios/${usuario.id}`
        });
    }, [createNotificationForAdmins]);

    const notifyAdminsSystemEvent = useCallback((evento, descripcion) => {
        createNotificationForAdmins({
            userId: 0,
            tipo: 'system',
            titulo: evento,
            mensaje: descripcion,
            refTipo: 'System',
            refId: null,
            ruta: null
        });
    }, [createNotificationForAdmins]);

    // ===== FUNCIONES PARA ALERTAS (Toasts locales) =====
    const alertError = useCallback((error) => { showError(error); }, [showError]);
    const alertSuccess = useCallback((message) => { showSuccess(message); }, [showSuccess]);
    const alertWarning = useCallback((message) => { showWarning(message); }, [showWarning]);
    const alertInfo = useCallback((message) => { showInfo(message); }, [showInfo]);
    const alertFileUploaded = useCallback((fileName) => { showSuccess(`Archivo "${fileName}" subido correctamente`); }, [showSuccess]);
    const alertConnectionError = useCallback(() => { showError('No se pudo conectar al servidor. Verifica tu conexión.'); }, [showError]);
    const alertSaveError = useCallback((error) => { showError(`Error al guardar cambios: ${error}`); }, [showError]);
    const alertRemoteConnection = useCallback((protocol, host) => { showInfo(`Iniciando conexión ${protocol} a ${host}...`); }, [showInfo]);


    const value = useMemo(() => ({
        // ===== FUNCIONES BASE =====
        createNotification,
        createNotificationForRole,
        createNotificationForAdmins,

        // ===== NOTIFICACIONES (Persistentes) =====
        notifyActivoCreated,
        notifyActivoAssigned,
        notifyTicketCreated,
        notifyTicketAssigned,
        notifyTicketUpdated,
        notifyUserCreated,
        notifySystemUpdate,
        notifyActivoReturned,

        // ===== NOTIFICACIONES DE PAZ Y SALVO =====
        notifyPazYSalvoCreated,
        notifyPazYSalvoEnviadoAFirma,
        notifyPazYSalvoFirmado,
        notifyPazYSalvoAprobado,
        notifyPazYSalvoRechazado,
        notifyPazYSalvoCerrado,
        notifyPazYSalvoObservado,
        notifyPazYSalvoExcepcionCreada,
        notifyPazYSalvoUploaded,

        // ===== NOTIFICACIONES DE ACTAS =====
        notifyActaRechazada,
        notifyActaFirmada,

        // ===== NOTIFICACIONES PARA ADMINS =====
        notifyAdminsTicketCreated,
        notifyAdminsActivoCreated,
        notifyAdminsUserCreated,
        notifyAdminsSystemEvent,

        // ===== ALERTAS (Temporales) =====
        alertError,
        alertSuccess,
        alertWarning,
        alertInfo,
        alertFileUploaded,
        alertConnectionError,
        alertSaveError,
        alertRemoteConnection,
    }), [
        createNotification, createNotificationForRole, createNotificationForAdmins,
        notifyActivoCreated, notifyActivoAssigned, notifyTicketCreated, notifyTicketAssigned,
        notifyTicketUpdated, notifyUserCreated, notifySystemUpdate, notifyActivoReturned,
        notifyPazYSalvoCreated, notifyPazYSalvoEnviadoAFirma, notifyPazYSalvoFirmado,
        notifyPazYSalvoAprobado, notifyPazYSalvoRechazado, notifyPazYSalvoCerrado,
        notifyPazYSalvoObservado, notifyPazYSalvoExcepcionCreada, notifyPazYSalvoUploaded,
        notifyActaRechazada, notifyActaFirmada,
        notifyAdminsTicketCreated, notifyAdminsActivoCreated, notifyAdminsUserCreated, notifyAdminsSystemEvent,
        alertError, alertSuccess, alertWarning, alertInfo,
        alertFileUploaded, alertConnectionError, alertSaveError, alertRemoteConnection,
    ]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}; 